import {Checkout, CheckoutFactory, IAddressData} from "@connox/typescript-library";
import {ICheckoutPutParams} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import PayPalCheckout from "braintree-web/paypal-checkout";
import * as React from "react";
import {useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {getShopId} from "../getShopId";
import window from "../WindowExtended";
import {PaymentController, Payments, paypalLocales} from "./PaymentController";
import {getLineItems} from "./PaypalModule";
import * as Sentry from "@sentry/browser";

interface IPaypal {
  Button: {
    render: (
      options: {
        env: string;
        locale: string;
        onAuthorize: (data: unknown) => Promise<void>;
        payment: () => Promise<unknown>;
        style: {
          [key: string]: string | number | boolean
        };
      },
      elementId: string,
    ) => void;
  };
}

interface ITokenizedData {
  nonce: string;
  details: {
    email: string;
    shippingAddress: {
      recipientName: string;
      line1: string;
      city: string;
      countryCode: string;
      postalCode: string;
    };
  };
}

interface IPaypalInstance {
  tokenizePayment: (data: unknown) => Promise<ITokenizedData>;
  createPayment: (options: unknown) => Promise<void>;
}

export const PayPalExpressButton: React.FC<{onReturn: () => void; handleError: (err: Error) => void}> = (props) => {
  const {cart} = useCartContext();
  const payPalButton = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!payPalButton.current) {
      return;
    }

    const locale = paypalLocales[getShopId()];
    let instance: null | IPaypalInstance = null;
    let checkout: Checkout | null = null;

    import("paypal-checkout").then(
      (paypal: IPaypal) =>
        paypal.Button.render(
          {
            env: __DEVELOPMENT__ ? "sandbox" : "production",
            locale,
            onAuthorize: async (data) => {
              /* AFTER COMPLETING PAYPAL CHECKOUT AND POPUP CLOSING */
              try {
                if (instance === null || checkout === null) {
                  throw new Error("Could not create PayPalCheckout instance");
                }
                const tokenized = await instance.tokenizePayment(data);

                // eslint-disable-next-line camelcase
                window.pp_nonce = tokenized.nonce;
                const {shippingAddress} = tokenized.details;
                const recipientName = shippingAddress.recipientName.split(" ");
                const line1Array = shippingAddress.line1.split(" ");
                const streetNumber = line1Array.pop();
                const street = line1Array.join(" ");
                const lastName = recipientName.pop();
                const firstName = recipientName.join(" ");

                const address: Partial<IAddressData> = {
                  city: shippingAddress.city,
                  countryCode: shippingAddress.countryCode,
                  email: tokenized.details.email,
                  firstName,
                  gender: "M",
                  lastName,
                  number: streetNumber,
                  street,
                  zip: shippingAddress.postalCode,
                };
                await checkout.put({
                  address: {
                    delivery: address,
                    invoice: address,
                  },
                  logisticsTypeId: cart.logisticsTypeId,
                } as ICheckoutPutParams);

                props.onReturn();
              } catch (err) {
                props.handleError(err);
              }

              /*/ region connox payment
            const response = await PaymentFactory.authorizePayment(payment.paymentTransactionId, nonce);
            // endregion connox payment */
            },
            payment: async () => {
              /* BEFORE OPENING POPUP */
              try {
                /*
                 * I'm not using checkoutContext on purpose making sure to only generate a checkout when button is
                 * actually clicked.
                 */
                checkout = await CheckoutFactory.generateCheckout(getShopId(), cart);

                await checkout.put({paymentId: Payments.PaypalExpressBraintree});
                PaymentController.createPayment(checkout, Payments.PaypalExpressBraintree);
                const {client} = await PaymentController.initBraintreeClient();
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                instance = (await PayPalCheckout.create({client})) as IPaypalInstance;

                if (instance === null) {
                  throw new Error("Could not create PayPalCheckout instance");
                }

                return instance.createPayment({
                  amount: checkout.cartData.value.payable.amount,
                  currency: checkout.cartData.currencyCode,
                  displayName: "connox - be unique",
                  enableShippingAddress: true,
                  flow: "checkout",
                  intent: "authorize",
                  lineItems: getLineItems(checkout.cartData),
                  locale,
                  shippingAddressEditable: true,
                });
              } catch (err) {
                props.handleError(err);
              }
            },
            style: {
              height: 40,
              label: "paypal",
              shape: "rect",
              size: "responsive",
              tagline: false,
            },
          },
          "#PaypalExpressButton",
        ),
      Sentry.captureException,
    );
  }, []);

  return <div id="PaypalExpressButton" ref={payPalButton} style={{marginBottom: "1rem"}} />;
};

export default PayPalExpressButton;
